import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, RotateCcw, Home } from "lucide-react";

/**
 * Class-based error boundary — the only way in React 18 to catch render-phase
 * errors from descendant components. Wraps the /admin/* subtree so a single
 * broken admin page (missing import, undefined field, etc.) doesn't take down
 * the whole console.
 */
export default class AdminErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("[AdminErrorBoundary]", error, errorInfo);
    this.setState({ errorInfo });
  }

  reset = () => {
    this.setState({ error: null, errorInfo: null });
  };

  render() {
    if (!this.state.error) return this.props.children;

    const message = this.state.error?.message || String(this.state.error);
    const stack = this.state.errorInfo?.componentStack || "";

    return (
      <div className="max-w-2xl" data-testid="admin-error-boundary">
        <p className="eyebrow text-red-400">Runtime error</p>
        <div className="mt-5 flex items-start gap-4">
          <AlertTriangle className="text-red-400 shrink-0 mt-1" size={28} />
          <div>
            <h1 className="font-display text-3xl md:text-4xl leading-tight tracking-tight text-[#FAF9F6]">
              This admin page crashed.
            </h1>
            <p className="mt-4 text-[#A9A9A6] text-sm leading-relaxed">
              The rest of the console is still working — use the sidebar to navigate away, or reset
              this view to try again. The error has been logged to the browser console for debugging.
            </p>
          </div>
        </div>

        <pre
          data-testid="admin-error-message"
          className="mt-8 p-4 bg-[#0B0B0C] border border-[#343437] text-red-400 text-xs whitespace-pre-wrap break-words font-mono"
        >
          {message}
        </pre>

        {stack && (
          <details className="mt-4">
            <summary className="text-[10px] tracking-[0.22em] uppercase text-[#A9A9A6] cursor-pointer hover:text-[#D8A936]">
              Stack trace
            </summary>
            <pre data-testid="admin-error-stack" className="mt-3 p-4 bg-[#0B0B0C] border border-[#343437] text-[#A9A9A6] text-[11px] whitespace-pre-wrap break-words font-mono max-h-96 overflow-auto">
              {stack.trim()}
            </pre>
          </details>
        )}

        <div className="mt-10 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={this.reset}
            data-testid="admin-error-retry"
            className="btn-primary"
          >
            <RotateCcw size={14} /> Try again
          </button>
          <Link to="/admin" data-testid="admin-error-home" className="btn-ghost">
            <Home size={14} /> Dashboard
          </Link>
        </div>
      </div>
    );
  }
}
